import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { LoanType } from '@/types/pool';

export type CounterOfferTab = 'documents' | 'poolTerms' | 'note';

type CounterOfferState = {
  // Pool data (for comparison)
  poolAmount: string;
  poolRate: string;
  poolTermMonths: string;
  poolLoanType: LoanType;

  // Form data
  counterAmount: string;
  counterRate: string;
  counterTermMonths: number;
  note: string;
  requiredDocuments: string[];

  // UI state
  activeTab: CounterOfferTab;
  showSuccess: boolean;

  // Validation errors
  errors: {
    amount?: string;
    rate?: string;
    term?: string;
  };

  // Actions
  setCounterAmount: (amount: string) => void;
  setCounterRate: (rate: string) => void;
  setCounterTermMonths: (months: number) => void;
  setNote: (note: string) => void;
  setRequiredDocuments: (documents: string[]) => void;
  setActiveTab: (tab: CounterOfferTab) => void;
  setShowSuccess: (show: boolean) => void;
  setErrors: (errors: { amount?: string; rate?: string; term?: string }) => void;
  clearError: (field: 'amount' | 'rate' | 'term') => void;

  // Initialize from pool
  initializeFromPool: (poolAmount: string, poolRate: string, poolLoanType: LoanType, poolTermMonths: number) => void;

  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;

  // Reset
  reset: () => void;
};

const initialState = {
  poolAmount: '',
  poolRate: '',
  poolTermMonths: '',
  poolLoanType: LoanType.AMORTIZING,
  counterAmount: '',
  counterRate: '',
  counterTermMonths: 0,
  note: '',
  requiredDocuments: [],
  activeTab: 'documents' as const,
  showSuccess: false,
  errors: {},
};

export const useCounterOfferStore = create<CounterOfferState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setCounterAmount: (amount) =>
        set((state) => ({
          counterAmount: amount,
          errors: { ...state.errors, amount: undefined },
        })),

      setCounterRate: (rate) =>
        set((state) => ({
          counterRate: rate,
          errors: { ...state.errors, rate: undefined },
        })),

      setCounterTermMonths: (months) =>
        set((state) => ({
          counterTermMonths: months,
          errors: { ...state.errors, term: undefined },
        })),

      setNote: (note) => set({ note }),
      setRequiredDocuments: (documents) => set({ requiredDocuments: documents }),

      setActiveTab: (tab) => set({ activeTab: tab }),

      setShowSuccess: (show) => set({ showSuccess: show }),

      setErrors: (errors) => set({ errors }),

      clearError: (field) =>
        set((state) => {
          const newErrors = { ...state.errors };
          delete newErrors[field];
          return { errors: newErrors };
        }),

      initializeFromPool: (poolAmount, poolRate, poolLoanType, poolTermMonths) => {
        const counterTermMonths = poolTermMonths;

        set({
          poolAmount,
          poolRate,
          poolTermMonths: poolTermMonths.toString(),
          poolLoanType,
          counterAmount: poolAmount,
          counterRate: poolRate,
          counterTermMonths,
        });
      },

      nextStep: () =>
        set((state) => {
          const tabOrder: Array<'documents' | 'poolTerms' | 'note'> = ['documents', 'poolTerms', 'note'];
          const currentIndex = tabOrder.indexOf(state.activeTab);
          if (currentIndex < tabOrder.length - 1) {
            return { activeTab: tabOrder[currentIndex + 1] };
          }
          return state;
        }),

      previousStep: () =>
        set((state) => {
          const tabOrder: Array<'documents' | 'poolTerms' | 'note'> = ['documents', 'poolTerms', 'note'];
          const currentIndex = tabOrder.indexOf(state.activeTab);
          if (currentIndex > 0) {
            return { activeTab: tabOrder[currentIndex - 1] };
          }
          return state;
        }),

      isFirstStep: () => {
        const state = get();
        return state.activeTab === 'documents';
      },

      isLastStep: () => {
        const state = get();
        return state.activeTab === 'note';
      },

      reset: () => set(initialState),
    }),
    { name: 'CounterOfferStore' }
  )
);
